import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import './CreateReviewPage.css';
import { createReview, getReviewByOrderItemId } from '../services/reviewService';

// Simple interactive star rating that supports half stars (click left/right half of a star)
// Updated to allow selecting 0 stars via hover/click and to treat hover=null as "no hover"
const StarRating = ({ rating, onChange }) => {
  const [hover, setHover] = useState(null);

  const getFillPercent = (index) => {
    const value = hover !== null ? hover : rating;
    if (value >= index + 1) return 100;
    if (value >= index + 0.5) return 50;
    return 0;
  };

  return (
    <div className="star-rating" role="radiogroup" aria-label="Rating">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = getFillPercent(i);
        const gradId = `grad-${i}`;
        return (
          <button
            key={i}
            type="button"
            className="star-btn"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percent = x / rect.width;
              // allow a 0 value when the user hovers the left area of the first star
              let val;
              if (i === 0 && percent < 0.25) {
                val = 0;
              } else {
                val = i + (percent >= 0.5 ? 1 : 0.5);
              }
              setHover(val);
            }}
            onMouseLeave={() => setHover(null)}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percent = x / rect.width;
              let value;
              if (i === 0 && percent < 0.25) {
                value = 0;
              } else {
                value = i + (percent >= 0.5 ? 1 : 0.5);
              }
              // clicking the same value toggles to 0 (allow deselect)
              if (value === rating) {
                onChange(0);
              } else {
                onChange(value);
              }
            }}
            aria-label={`${i + 1} star`}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <linearGradient id={gradId} x1="0%" x2="100%">
                  <stop offset={`${fill}%`} stopColor="#FFA500" />
                  <stop offset={`${fill}%`} stopColor="#eee" />
                </linearGradient>
              </defs>
              <path fill={`url(#${gradId})`} d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

const CreateReviewPage = () => {
  const { orderItemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  // vendorId should be passed via state when navigating here
  const vendorId = location.state?.vendorId;
  const vendorName = location.state?.vendorName;

  const [rating, setRating] = useState(5.0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const TITLE_MAX = 100;
  const DESCRIPTION_MAX = 500;
  const [submitting, setSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadExisting = async () => {
      if (!orderItemId) return;
      try {
        const rev = await getReviewByOrderItemId(orderItemId);
        setExistingReview(rev);
      } catch (err) {
        console.error('Error checking existing review:', err);
      }
    };
    loadExisting();
  }, [orderItemId]);

  const validate = () => {
    const newErrors = {};
    // allow 0.0 as a valid rating now
    if (rating === null || rating === undefined || rating < 0 || rating > 5) newErrors.rating = 'Please provide a rating between 0.0 and 5.0.';
    if (!title || title.trim() === '') newErrors.title = 'Please enter a title for your review.';
    if (!description || description.trim() === '') newErrors.description = 'Please enter a description for your review.';
    if (!orderItemId) newErrors.orderItem = 'Order item missing.';
    if (!vendorId) newErrors.vendor = 'Vendor information missing.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validate()) {
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    if (!vendorId) {
      setError('Vendor information missing. Please go back to the order page to leave a review.');
      return;
    }

    if (existingReview) {
      setError('A review already exists for this order item.');
      return;
    }

    try {
      setSubmitting(true);
      await createReview({
        reviewerId: user.$id,
        reviewedId: vendorId,
        rating: rating,
        title: title,
        description: description,
        orderItemId: orderItemId
      });

      showSuccess('Thanks for your review!');
      navigate(-1); // go back to previous page (order detail)
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.message ? `Failed to submit review: ${err.message}` : 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-review-page">
      <TopBar />
      <div className="create-review-container">
        <button className="back-link" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="page-title">Leave a Review</h1>
        <p className="subtitle">Share your experience with {vendorName || 'this vendor'}.</p>

        {existingReview ? (
          <div className="already-reviewed">
            <p>You've already left a review for this item. Thank you!</p>
            <div className="review-summary">
              <strong>{existingReview.title}</strong>
              <p>{existingReview.description}</p>
              <p>Rating: {existingReview.rating}</p>
            </div>
          </div>
        ) : (
          <form className="review-form" onSubmit={handleSubmit}>
            {(errors.vendor || errors.orderItem) && <div className="form-error">{errors.vendor || errors.orderItem}</div>}

            <div className="form-row">
              <label>Rating <span className="required">*</span></label>
              <div className="rating-input">
                <StarRating rating={rating} onChange={(v) => { setRating(v); setErrors(prev => { const cp = { ...prev }; delete cp.rating; return cp; }); }} />
                <select
                  className="rating-select"
                  value={rating}
                  onChange={(e) => { const v = parseFloat(e.target.value); setRating(v); setErrors(prev => { const cp = { ...prev }; delete cp.rating; return cp; }); }}
                  aria-label="Numeric rating select"
                >
                  <option value={0.0}>0.0</option>
                  <option value={0.5}>0.5</option>
                  
                  <option value={1.0}>1.0</option>
                  <option value={1.5}>1.5</option>
                  <option value={2.0}>2.0</option>
                  <option value={2.5}>2.5</option>
                  <option value={3.0}>3.0</option>
                  <option value={3.5}>3.5</option>
                  <option value={4.0}>4.0</option>
                  <option value={4.5}>4.5</option>
                  <option value={5.0}>5.0</option>
                </select>
              </div>
              {errors.rating && <div className="field-error">{errors.rating}</div>}
            </div>

            <div className="form-row">
              <label>Title <span className="required">*</span></label>
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors(prev => { const cp = { ...prev }; delete cp.title; return cp; }); }}
                  placeholder="Short title for your review"
                  maxLength={TITLE_MAX}
                  aria-describedby="title-counter"
                />
                <div id="title-counter" className={`char-counter ${title.length >= TITLE_MAX - 10 ? 'warning' : ''}`} aria-live="polite">
                  {TITLE_MAX - title.length} characters left
                </div>
                {errors.title && <div className="field-error">{errors.title}</div>}
              </div>
            </div>

            <div className="form-row">
              <label>Description <span className="required">*</span></label>
              <div>
                <textarea
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); setErrors(prev => { const cp = { ...prev }; delete cp.description; return cp; }); }}
                  placeholder="Describe your experience"
                  rows={6}
                  maxLength={DESCRIPTION_MAX}
                  aria-describedby="description-counter"
                />
                <div id="description-counter" className={`char-counter ${description.length >= DESCRIPTION_MAX - 20 ? 'warning' : ''}`} aria-live="polite">
                  {DESCRIPTION_MAX - description.length} characters left
                </div>
                {errors.description && <div className="field-error">{errors.description}</div>}
              </div>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        )}
      </div>

    </div>
  );
};

export default CreateReviewPage;
